import type { MouseEvent } from 'react';
import { Check, ChevronRight, Loader2 } from 'lucide-react';
import { cn, formatWorkflowStateLabel } from '@/lib/utils';
import type { ExecutionLogEntry, StageDetail, TimelineStageKey } from '@/api/cockpit';

export interface TimelineSubstepSelection {
  id: string;
  label: string;
  kind: 'internal';
  tone: 'info' | 'success' | 'warning' | 'danger';
}

export interface TimelineActiveStep {
  stageKey: TimelineStageKey;
  substepId?: string | null;
}

// After commit 3c collapsed the transient `<X-ing>` claim states, each
// stage's `statuses` only enumerates the waiting and outcome states the
// pipeline can land on. Worker-in-flight execution is no longer a
// distinct status; it is signalled by `claimed_by` being non-null on a
// waiting row.
export const TIMELINE_STAGES = [
  {
    key: 'ingestion',
    label: 'Ingestion',
    statuses: ['New'],
  },
  {
    key: 'ocr',
    label: 'OCR',
    statuses: ['Waiting for OCR Extraction', 'OCR Low Confidence'],
  },
  {
    key: 'classification',
    label: 'Classification',
    statuses: ['Waiting for Classification', 'Classified as Non-Purchase Order'],
  },
  {
    key: 'duplicate',
    label: 'Duplicate Check',
    statuses: [
      'Waiting for Duplicate Check',
      'Exact Duplicate',
      'Suspected Duplicate',
      'Waiting on Source Document',
    ],
  },
  {
    key: 'matching',
    label: 'Matching',
    statuses: [
      'Waiting for Legal Entity Matching',
      'Waiting for Site Matching',
      'Waiting for Matching Fanout',
      'Waiting for Customer Matching',
      'Customer Matched',
      'Customer Ambiguous',
      'Customer Not Found',
      'Waiting for Product Matching',
      'Product Ambiguous',
      'Product Not Found',
      'Waiting for Payment Terms Mapping',
      'Waiting for Delivery Terms Mapping',
      'Waiting for Delivery Mode Mapping',
    ],
  },
  {
    key: 'validation',
    label: 'Validation',
    statuses: ['Waiting for Validation', 'Validated', 'Validation Failed - Blocking'],
  },
  {
    key: 'draft',
    label: 'Creating Draft in D365',
    // 'Draft Created' / 'Confirmed' / 'Confirmation Failed' are legacy
    // pre-approval rest stops, kept here so historical rows still bind
    // to the draft stage on the timeline.
    statuses: [
      'Waiting for Draft Creation',
      'Draft Created',
      'Draft Creation Failed',
      'Confirmed',
      'Confirmation Failed',
    ],
  },
  {
    key: 'validating_in_d365',
    label: 'Validating in D365',
    // USE_STAGING_ENTITY_FLOW: the staging-poller worker watches
    // `tpmSFSyncStatus` on `tpmDI_DirectSalesOrders` after draft creation
    // wrote the row with `ValidationOnly=Yes`. Resolved to either
    // 'Awaiting Operator Approval' (Synced) or 'Validation Failed' (Error).
    statuses: ['Validating in D365', 'Validation Failed'],
  },
  {
    key: 'approval',
    label: 'Approval',
    // 'Approved' / 'Waiting for Acknowledgement' / 'Acknowledgement Sent'
    // / 'Acknowledgement Failed' are legacy: the automation no longer
    // sends acknowledgement emails, and operator approve advances
    // directly to Done.
    statuses: [
      'Awaiting Operator Approval',
      'Approved',
      'Waiting for Acknowledgement',
      'Acknowledgement Sent',
      'Acknowledgement Failed',
    ],
  },
  {
    key: 'creating_sales_order',
    label: 'Convert Draft to Sales Order in D365',
    // Historical USE_STAGING_ENTITY_FLOW state. Current approvals create the
    // real order directly through SalesOrderHeadersV4 + SalesOrderLinesV3.
    statuses: ['Creating Sales Order', 'Sales Order Creation Failed'],
  },
  {
    key: 'resolved',
    label: 'Resolved',
    statuses: ['Done', 'Closed', 'Closed - Operator', 'Rejected'],
  },
] as const satisfies ReadonlyArray<{
  key: TimelineStageKey;
  label: string;
  statuses: readonly string[];
}>;

export function findTimelineStageIndex(
  status: string,
  currentStageKey?: TimelineStageKey | null,
): number {
  const effectiveKey = currentStageKey ?? 'matching';
  const index = TIMELINE_STAGES.findIndex((stage) => stage.key === effectiveKey);
  if (index >= 0) return index;
  return TIMELINE_STAGES.findIndex((stage) => stage.key === 'matching');
}

type CurrentTimelineTone = 'info' | 'warning' | 'danger';

function getCurrentTimelineTone(currentStatus: string): CurrentTimelineTone {
  const normalizedStatus = currentStatus.toLowerCase();
  if (
    normalizedStatus.includes('failed') ||
    normalizedStatus.includes('error') ||
    normalizedStatus.includes('exception') ||
    normalizedStatus === 'retries exhausted'
  ) {
    return 'danger';
  }
  if (
    normalizedStatus.includes('awaiting operator') ||
    normalizedStatus.includes('under review') ||
    normalizedStatus.includes('ambiguous') ||
    normalizedStatus.includes('not found') ||
    normalizedStatus.includes('low confidence')
  ) {
    return 'warning';
  }
  return 'info';
}

function getCurrentStageMarkerClassName(tone: CurrentTimelineTone): string {
  switch (tone) {
    case 'danger':
      return 'border-destructive bg-destructive text-destructive-foreground shadow-sm';
    case 'warning':
      return 'border-warning bg-warning text-warning-foreground shadow-sm';
    case 'info':
      return 'border-primary bg-primary text-primary-foreground shadow-sm';
  }
}

function getCurrentConnectorClassName(tone: CurrentTimelineTone): string {
  switch (tone) {
    case 'danger':
      return 'bg-destructive/40';
    case 'warning':
      return 'bg-warning/45';
    case 'info':
      return 'bg-primary/35';
  }
}

function getCurrentStatusTextClassName(tone: CurrentTimelineTone): string {
  switch (tone) {
    case 'danger':
      return 'text-destructive';
    case 'warning':
      return 'text-warning-foreground';
    case 'info':
      return 'text-muted-foreground';
  }
}

function getSubstepMarkerClassName(
  step: TimelineSubstepSelection,
  isLastInLinearList: boolean,
  selected: boolean,
  active: boolean,
): string {
  return cn(
    'flex h-3 w-3 items-center justify-center rounded-full border-2 bg-background',
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : step.tone === 'success'
        ? 'border-emerald-600 bg-emerald-600 text-white'
        : step.tone === 'danger'
          ? 'border-destructive bg-destructive text-destructive-foreground'
        : step.tone === 'warning'
          ? 'border-warning bg-warning text-warning-foreground'
          : isLastInLinearList
            ? 'border-primary'
            : 'border-primary/80',
    selected && 'ring-2 ring-primary/25 ring-offset-2 ring-offset-background',
  );
}

function SubstepButton({
  stageKey,
  step,
  active,
  isLastInLinearList,
  selectedSubstepId,
  onSubstepClick,
  onSubstepContextMenu,
}: {
  stageKey: TimelineStageKey;
  step: TimelineSubstepSelection;
  active: boolean;
  isLastInLinearList: boolean;
  selectedSubstepId: string | null | undefined;
  onSubstepClick:
    | ((stageKey: TimelineStageKey, substep: TimelineSubstepSelection) => void)
    | undefined;
  onSubstepContextMenu:
    | ((event: MouseEvent, stageKey: TimelineStageKey, substep: TimelineSubstepSelection) => void)
    | undefined;
}): JSX.Element {
  const selected = selectedSubstepId === `${stageKey}:${step.id}`;

  return (
    <button
      type="button"
      aria-disabled={!onSubstepClick}
      onClick={() => {
        if (!onSubstepClick) return;
        onSubstepClick(stageKey, step);
      }}
      onContextMenu={(event) => {
        event.stopPropagation();
        if (!onSubstepContextMenu) return;
        event.preventDefault();
        onSubstepContextMenu(event, stageKey, step);
      }}
      className={cn(
        'flex flex-col items-center rounded-md px-1 py-0.5 text-center transition-colors',
        onSubstepClick ? 'cursor-pointer hover:bg-muted/40' : 'cursor-default',
        selected && 'bg-muted/50',
      )}
    >
      <div className={getSubstepMarkerClassName(step, isLastInLinearList, selected, active)}>
        {active ? <Loader2 className="h-2 w-2 animate-spin" /> : null}
        {!active && step.tone === 'success' ? <Check className="h-2 w-2" /> : null}
        {!active && step.tone === 'danger' ? <span className="text-[9px] font-bold leading-none">X</span> : null}
        {!active && step.tone === 'warning' ? <span className="text-[9px] font-bold leading-none">!</span> : null}
      </div>
      <div className="mt-0.5 w-[5.5rem] truncate text-center text-[10px] leading-none text-muted-foreground">
        {step.label}
      </div>
    </button>
  );
}

function LinearSubstepList({
  stageKey,
  substeps,
  activeSubstepIds,
  selectedSubstepId,
  onSubstepClick,
  onSubstepContextMenu,
}: {
  stageKey: TimelineStageKey;
  substeps: TimelineSubstepSelection[];
  activeSubstepIds: ReadonlySet<string>;
  selectedSubstepId: string | null | undefined;
  onSubstepClick:
    | ((stageKey: TimelineStageKey, substep: TimelineSubstepSelection) => void)
    | undefined;
  onSubstepContextMenu:
    | ((event: MouseEvent, stageKey: TimelineStageKey, substep: TimelineSubstepSelection) => void)
    | undefined;
}): JSX.Element {
  return (
    <div className="space-y-2">
      {substeps.map((step, substepIndex) => (
        <div key={`${stageKey}-${step.id}`} className="flex flex-col items-center">
          <SubstepButton
            stageKey={stageKey}
            step={step}
            active={activeSubstepIds.has(step.id)}
            isLastInLinearList={substepIndex === substeps.length - 1}
            selectedSubstepId={selectedSubstepId}
            onSubstepClick={onSubstepClick}
            onSubstepContextMenu={onSubstepContextMenu}
          />
          {substepIndex < substeps.length - 1 ? (
            <div className="mt-1 h-5 w-px bg-primary/35" />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function MatchingFanoutTree({
  substeps,
  activeSubstepIds,
  selectedSubstepId,
  onSubstepClick,
  onSubstepContextMenu,
}: {
  substeps: TimelineSubstepSelection[];
  activeSubstepIds: ReadonlySet<string>;
  selectedSubstepId: string | null | undefined;
  onSubstepClick:
    | ((stageKey: TimelineStageKey, substep: TimelineSubstepSelection) => void)
    | undefined;
  onSubstepContextMenu:
    | ((event: MouseEvent, stageKey: TimelineStageKey, substep: TimelineSubstepSelection) => void)
    | undefined;
}): JSX.Element {
  // FFS-130 #744 follow-up: site_matching now runs AFTER the parallel
  // fanout (it consumes the per-line product match_events). Visually the
  // trunk is the single pre-fanout step (legal_entity_matching), the
  // fanout branch fans out from there, and site_matching + draft_rejected
  // sit sequentially below the fanout in the post-fanout column.
  const postFanoutStepIds = new Set(['site_matching', 'draft_rejected', 'draft_generation_error']);
  const postFanoutSteps = substeps.filter((step) => postFanoutStepIds.has(step.id));
  const matchingSteps = substeps.filter((step) => !postFanoutStepIds.has(step.id));
  const legalIndex = matchingSteps.findIndex((step) => step.id === 'legal_entity_matching');
  const splitIndex = legalIndex >= 0 ? legalIndex + 1 : Math.min(1, matchingSteps.length);
  const trunkSteps = matchingSteps.slice(0, splitIndex);
  const fanoutSteps = matchingSteps.slice(splitIndex);

  if (fanoutSteps.length === 0) {
    return (
      <LinearSubstepList
        stageKey="matching"
        substeps={substeps}
        activeSubstepIds={activeSubstepIds}
        selectedSubstepId={selectedSubstepId}
        onSubstepClick={onSubstepClick}
        onSubstepContextMenu={onSubstepContextMenu}
      />
    );
  }

  return (
    <div className="absolute left-1/2 top-0 flex w-max -translate-x-1/2 flex-col items-center">
      {trunkSteps.map((step, substepIndex) => (
        <div key={`matching-${step.id}`} className="flex flex-col items-center">
          <SubstepButton
            stageKey="matching"
            step={step}
            active={activeSubstepIds.has(step.id)}
            isLastInLinearList={false}
            selectedSubstepId={selectedSubstepId}
            onSubstepClick={onSubstepClick}
            onSubstepContextMenu={onSubstepContextMenu}
          />
          <div className="mt-0.5 h-4 w-px bg-primary/35" />
        </div>
      ))}
      <div className="relative pt-4">
        <div className="absolute left-[2.75rem] right-[2.75rem] top-0 h-px bg-primary/35" />
        <div className="grid grid-flow-col auto-cols-[5.5rem] justify-center gap-2">
          {fanoutSteps.map((step) => (
            <div
              key={`matching-${step.id}`}
              className="relative flex flex-col items-center before:absolute before:left-1/2 before:top-[-1rem] before:h-4 before:w-px before:-translate-x-1/2 before:bg-primary/35"
            >
              <SubstepButton
                stageKey="matching"
                step={step}
                active={activeSubstepIds.has(step.id)}
                isLastInLinearList
                selectedSubstepId={selectedSubstepId}
                onSubstepClick={onSubstepClick}
                onSubstepContextMenu={onSubstepContextMenu}
              />
            </div>
          ))}
        </div>
      </div>
      {postFanoutSteps.length > 0 ? (
        <div className="flex flex-col items-center">
          <div className="mt-0.5 h-4 w-px bg-primary/35" />
          {postFanoutSteps.map((step, stepIndex) => (
            <div key={`matching-${step.id}`} className="flex flex-col items-center">
              <SubstepButton
                stageKey="matching"
                step={step}
                active={activeSubstepIds.has(step.id)}
                isLastInLinearList={stepIndex === postFanoutSteps.length - 1}
                selectedSubstepId={selectedSubstepId}
                onSubstepClick={onSubstepClick}
                onSubstepContextMenu={onSubstepContextMenu}
              />
              {stepIndex < postFanoutSteps.length - 1 ? (
                <div className="mt-0.5 h-4 w-px bg-primary/35" />
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function isTimelineStatusExecuting(status: string): boolean {
  return EXECUTING_STATUSES.has(status);
}

// After commit 3c collapsed the transient `<X-ing>` claim states, the only
// statuses that visibly mean "execution in progress" are the operator-driven
// re-review and reprocessing states. Worker-in-flight execution for a normal
// claim is now signalled out-of-band via the live `activeSteps` props
// (`/cockpit/active-steps`), keyed by the staging row's stage and substep.
function getCurrentStatusActiveSubstepIds(_currentStatus: string): readonly string[] {
  return [];
}

const EXECUTING_STATUSES = new Set(['Exception - Under Review', 'Reprocessing']);

export function StateMachineTimeline({
  currentStatus,
  className,
  selectedStageKey,
  onStageClick,
  onStageContextMenu,
  onSubstepClick,
  onSubstepContextMenu,
  currentStageKey,
  stageDetails,
  selectedSubstepId,
  showSubsteps = false,
  activeSteps = [],
}: {
  currentStatus: string;
  className?: string;
  selectedStageKey?: TimelineStageKey | null;
  onStageClick?: (stageKey: TimelineStageKey) => void;
  onStageContextMenu?: (event: MouseEvent, stageKey: TimelineStageKey) => void;
  onSubstepClick?: (stageKey: TimelineStageKey, substep: TimelineSubstepSelection) => void;
  onSubstepContextMenu?: (
    event: MouseEvent,
    stageKey: TimelineStageKey,
    substep: TimelineSubstepSelection,
  ) => void;
  currentStageKey?: TimelineStageKey | null;
  stageDetails?: Partial<Record<TimelineStageKey, StageDetail>>;
  selectedSubstepId?: string | null;
  showSubsteps?: boolean;
  activeSteps?: readonly TimelineActiveStep[];
}): JSX.Element {
  const currentIndex = findTimelineStageIndex(currentStatus, currentStageKey);
  const currentTone = getCurrentTimelineTone(currentStatus);
  const liveActiveStageKeys = new Set(activeSteps.map((step) => step.stageKey));
  const liveActiveSubstepKeys = new Set(
    activeSteps
      .filter((step) => step.substepId)
      .map((step) => `${step.stageKey}:${step.substepId}`),
  );
  const currentStageForStatus = currentStageKey ?? TIMELINE_STAGES[currentIndex]?.key ?? 'matching';
  const liveActiveSubstepKeysForCurrentStage = [...liveActiveSubstepKeys].filter((key) =>
    key.startsWith(`${currentStageForStatus}:`),
  );
  const inferredActiveSubstepKeys = new Set(
    (liveActiveSubstepKeysForCurrentStage.length > 0
      ? []
      : getCurrentStatusActiveSubstepIds(currentStatus)
    ).map((substepId) => `${currentStageForStatus}:${substepId}`),
  );

  return (
    <div className={cn('overflow-visible pb-2', className)}>
      <div className="flex min-w-max items-start gap-0 overflow-visible">
        {TIMELINE_STAGES.map((stage, index) => {
          const state =
            index < currentIndex ? 'complete' : index === currentIndex ? 'current' : 'upcoming';
          const clickable = state !== 'upcoming' && Boolean(onStageClick);
          const isSelected = selectedStageKey === stage.key;
          const detail = stageDetails?.[stage.key];
          const substeps: TimelineSubstepSelection[] = detail
            ? detail.internalSteps.map((step, stepIndex) => ({
                id: step.id ?? `internal-${stepIndex}`,
                label: step.title,
                kind: 'internal' as const,
                tone: step.tone,
              }))
            : [];
          const hasSubstages = state !== 'upcoming' && substeps.length > 0;
          const showSubstages = showSubsteps && hasSubstages;
          const isStageExecuting =
            state === 'current' &&
            (isTimelineStatusExecuting(currentStatus) || liveActiveStageKeys.has(stage.key));
          const activeSubstepIds = new Set(
            [...liveActiveSubstepKeys, ...inferredActiveSubstepKeys]
              .filter((key) => key.startsWith(`${stage.key}:`))
              .map((key) => key.slice(stage.key.length + 1)),
          );

          return (
            <div key={stage.key} className="flex items-start">
              <div className="relative flex min-w-[5.75rem] flex-col items-center overflow-visible">
                <button
                  type="button"
                  aria-disabled={!clickable}
                  onClick={() => {
                    if (!clickable) return;
                    onStageClick?.(stage.key);
                  }}
                  onContextMenu={(event) => {
                    event.stopPropagation();
                    if (!clickable || !onStageContextMenu) return;
                    event.preventDefault();
                    onStageContextMenu(event, stage.key);
                  }}
                  className={cn(
                    'flex w-full flex-col items-center gap-1.5 rounded-md px-1 py-1 text-center transition-colors',
                    clickable && 'cursor-pointer hover:bg-muted/40',
                    !clickable && 'cursor-default',
                    isSelected && 'bg-muted/50',
                  )}
                >
                  <div className="flex h-7 items-center justify-center">
                    <div
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-semibold transition-colors',
                        state === 'complete' && 'border-emerald-600 bg-emerald-600 text-white',
                        state === 'current' && getCurrentStageMarkerClassName(currentTone),
                        state === 'upcoming' && 'border-border bg-background text-muted-foreground',
                        isSelected && 'ring-2 ring-primary/30 ring-offset-2 ring-offset-background',
                      )}
                    >
                      {state === 'complete' ? <Check className="h-3.5 w-3.5" /> : null}
                      {state !== 'complete' && isStageExecuting ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : null}
                      {state !== 'complete' && !isStageExecuting ? index + 1 : null}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div
                      className={cn(
                        'text-[11px] font-medium leading-tight',
                        state === 'upcoming' ? 'text-muted-foreground' : 'text-foreground',
                      )}
                    >
                      {stage.label}
                    </div>
                    {state === 'current' ? (
                      <div
                        className={cn(
                          'text-[10px] leading-tight',
                          getCurrentStatusTextClassName(currentTone),
                        )}
                      >
                        {formatWorkflowStateLabel(currentStatus)}
                      </div>
                    ) : null}
                  </div>
                </button>
                {hasSubstages ? (
                  <div
                    className={cn(
                      'w-full transition-[max-height,opacity,margin] duration-300 ease-out',
                      stage.key === 'matching' ? 'overflow-visible' : 'overflow-hidden',
                      showSubstages ? 'mt-2 max-h-[32rem] opacity-100' : 'mt-0 max-h-0 opacity-0',
                    )}
                    aria-hidden={!showSubstages}
                  >
                    <div
                      className={cn(
                        'relative flex flex-col items-center pb-1',
                        stage.key === 'matching' && 'min-h-[11.75rem]',
                      )}
                    >
                      <div
                        className={cn(
                          'h-4 w-px',
                          state === 'current'
                            ? getCurrentConnectorClassName(currentTone)
                            : 'bg-primary/35',
                        )}
                      />
                      {stage.key === 'matching' ? (
                        <MatchingFanoutTree
                          substeps={substeps}
                          activeSubstepIds={activeSubstepIds}
                          selectedSubstepId={selectedSubstepId}
                          onSubstepClick={onSubstepClick}
                          onSubstepContextMenu={onSubstepContextMenu}
                        />
                      ) : (
                        <LinearSubstepList
                          stageKey={stage.key}
                          substeps={substeps}
                          activeSubstepIds={activeSubstepIds}
                          selectedSubstepId={selectedSubstepId}
                          onSubstepClick={onSubstepClick}
                          onSubstepContextMenu={onSubstepContextMenu}
                        />
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
              {index < TIMELINE_STAGES.length - 1 ? (
                <div className="flex h-7 w-5 flex-none items-center justify-center">
                  <ChevronRight
                    className={cn(
                      'h-4 w-4',
                      index < currentIndex ? 'text-emerald-600' : 'text-border',
                    )}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
